import { match } from '@st/pattern'
import { Box, Checkbox, Column, Expand, Row, Space, Text } from './document-elements'
import { FORM_BORDER_GREY } from './form'
import {
  Folder,
  STCategory,
  STChecklistItem,
  STDocument,
  STDocumentType,
  STMissingReason
} from '@st/sdk'
import { paginate, layoutPages } from '@st/document-rendering'

export const CHECKLIST_ITEMS_PER_PAGE = 25

export function paginateChecklistItems(checklistItems: STChecklistItem[]) {
  const phases = ['intake', 'delivery'] as const
  let offset = 0
  return phases.flatMap((phase) => {
    const pages = paginate(
      checklistItems.filter((item) => item.phase == phase),
      CHECKLIST_ITEMS_PER_PAGE
    )
    const title = checklistTitle(phase)
    const result = pages.map((page, index) => ({
      ...page,
      number: index + 1 + offset,
      phase,
      title
    }))
    offset += pages.length
    return result
  })
}

type Props = {
  folder: Folder
  entityName: string
  checklistItems: STChecklistItem[]
  documents: STDocument[]
  documentTypes: STDocumentType[]
  categories: STCategory[]
}
export function renderChecklistPages({
  folder,
  entityName,
  checklistItems,
  documents,
  documentTypes,
  categories
}: Props) {
  const missingReasonsById = selMissingReasonsById({ documentTypes, categories })
  return (['intake', 'delivery'] as const).flatMap((phase) => {
    const rows = selectChecklistRows({
      items: checklistItems.filter((item) => item.phase == phase),
      documents,
      missingReasonsById
    })
    return layoutPages({
      pageSize: { width: 800, height: 1000 },
      padding: { top: 60, bottom: 60, left: 80, right: 80 },
      children: [
        <ChecklistHeading
          key="heading"
          entityName={entityName}
          title={checklistTitle(phase)}
          type={folder.type}
          year={folder.year}
        />,
        ...rows.map((row) => <ChecklistRow key={row.item.id} {...row} />)
      ]
    })
  })
}

function ChecklistHeading(props: {
  entityName: string
  title: string
  type: string
  year: number
}) {
  return (
    <Box padding={{ top: 0, bottom: 40, left: 0, right: 0 }}>
      <Row>
        <Expand flex={4}>
          <Column mainAxisSize="min">
            <Text fontSize={24}>{props.entityName}</Text>
            <Space h={8} />
            <Text fontSize={14}>{props.title}</Text>
          </Column>
        </Expand>
        <Expand>
          <Box height={32} hAlign="end">
            <Column align="end">
              <Text fontSize={10}>{`RETURN TYPE: ${props.type}`}</Text>
              <Space h={8} />
              <Text fontSize={10}>{`TAX YEAR: ${props.year}`}</Text>
            </Column>
          </Box>
        </Expand>
      </Row>
    </Box>
  )
}

function ChecklistRow({
  item,
  attachmentLabel,
  statusLabel
}: ReturnType<typeof selectChecklistRows>[number]) {
  const status = match(item.context)
    .with({ type: 'tax_payment' }, ({ amount }) => {
      if (amount == '') return ''
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
        Number(amount)
      )
    })
    .otherwise(() => attachmentLabel || statusLabel)
  const statusColor = match(attachmentLabel)
    .with('', () => '#848796')
    .otherwise(() => '#354dcc')
  return (
    <Box
      borderColor={FORM_BORDER_GREY}
      borderWidth={0.5}
      padding={{ top: 6, bottom: 4, left: 6, right: 6 }}
    >
      <Row gap={12}>
        <Box height={10} width={20} hAlign="center" vAlign="start">
          <Checkbox name={item.id} value={item.status == 'complete'} />
        </Box>
        <Expand flex={3}>
          <Column mainAxisSize="min">
            <Text fontSize={10} lineThrough={item.cancelled}>
              {item.name}
            </Text>
            {item.dueOn && <Text fontSize={8}>{`Due ${item.dueOn}`}</Text>}
          </Column>
        </Expand>
        <Expand flex={4}>
          <Text fontSize={10}>{item.note ?? ''}</Text>
        </Expand>
        <Expand flex={6}>
          <Text fontSize={10} color={statusColor}>
            {status}
          </Text>
        </Expand>
      </Row>
    </Box>
  )
}

function checklistTitle(phase: 'intake' | 'delivery') {
  return match(phase)
    .with('intake', () => 'Intake checklist')
    .with('delivery', () => 'Tax payment checklist')
    .exhaustive()
}

function selectChecklistRows({
  items,
  documents,
  missingReasonsById
}: {
  items: STChecklistItem[]
  documents: STDocument[]
  missingReasonsById: Record<string, STMissingReason>
}) {
  return items.map((item) => ({
    item,
    attachmentLabel: documents
      .filter((document) =>
        item.attachments.some((reference) => reference.documentId == document.id)
      )
      .map((document) => document.name)
      .join(', '),
    statusLabel: missingReasonsById[item.incompleteReason ?? '']?.name ?? ''
  }))
}

function selMissingReasonOptionsMap(state: {
  documentTypes: STDocumentType[]
  categories: STCategory[]
}): Record<string, STMissingReason[]> {
  const reasonsById: Record<string, STMissingReason[]> = {}

  for (const cat of state.categories) {
    reasonsById[cat.id] = cat.missingReasons
  }
  for (const documentType of state.documentTypes) {
    reasonsById[documentType.id] = documentType.missingReasons
  }

  return reasonsById
}

function selMissingReasonsById(state: {
  documentTypes: STDocumentType[]
  categories: STCategory[]
}): Record<string, STMissingReason> {
  const map: Record<string, STMissingReason> = {}

  for (const reasons of Object.values(selMissingReasonOptionsMap(state))) {
    for (const r of reasons) {
      map[r.key] = r
    }
  }
  return map
}
